// 워크스페이스 — 바닐라 dcf.html #page-workspace 이식 (Phase 2 완성 B).
// 좌측 DCF AI 채팅 패널(접기/펼치기·너비 조절·localStorage 유지) + 우측 7개 탭.
// 탭 전환은 바닐라 tab-pane 방식(display 토글)으로 컴포넌트를 언마운트하지 않아
// WACC 산출 결과·초안 등 탭 로컬 상태가 유지된다.
// 저장 버튼은 자동 버전 스냅샷 포함(saveWithVersion), 버전 패널 토글·복원 지원.
import { useEffect, useRef, useState } from "react";
import { useT } from "../../lib/i18n";
import { useDcf } from "../../stores/dcf";
import { Button } from "../../ui/Button";
import AssumptionsTab from "./AssumptionsTab";
import ChatPanel from "./ChatPanel";
import ProjectionTab from "./ProjectionTab";
import ReportTab from "./ReportTab";
import ResultTab from "./ResultTab";
import SensitivityTab from "./SensitivityTab";
import SummaryTab from "./SummaryTab";
import VersionsPanel from "./VersionsPanel";
import WaccTab from "./WaccTab";

type TabId =
  | "summary"
  | "assumptions"
  | "projection"
  | "wacc"
  | "dcf"
  | "sensitivity"
  | "report";

const TABS: Array<{ id: TabId; labelKey: string }> = [
  { id: "summary", labelKey: "tab.summary" },
  { id: "assumptions", labelKey: "tab.assumptions" },
  { id: "projection", labelKey: "tab.projection" },
  { id: "wacc", labelKey: "tab.wacc" },
  { id: "dcf", labelKey: "tab.dcf" },
  { id: "sensitivity", labelKey: "tab.sensitivity" },
  { id: "report", labelKey: "tab.report" },
];

// 바닐라 chatResizer 의 너비 규칙 — MIN 260, 기본 360, 최대 뷰포트 70%
const CHAT_MIN_W = 260;
const CHAT_DEF_W = 360;
const COLLAPSE_KEY = "va_dcf_chat_collapsed";
const WIDTH_KEY = "va_dcf_chat_w";

function loadChatCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function clampChatW(px: number): number {
  const max = Math.max(
    CHAT_DEF_W,
    Math.round((typeof window !== "undefined" ? window.innerWidth : 1280) * 0.7),
  );
  return Math.max(CHAT_MIN_W, Math.min(max, px));
}

function loadChatW(): number {
  try {
    const w = parseInt(localStorage.getItem(WIDTH_KEY) || String(CHAT_DEF_W), 10);
    return clampChatW(w || CHAT_DEF_W);
  } catch {
    return CHAT_DEF_W;
  }
}

export default function DcfWorkspace({ onBack }: { onBack: () => void }) {
  const { t } = useT();
  const current = useDcf((s) => s.current);
  const recalc = useDcf((s) => s.recalc);
  const saveWithVersion = useDcf((s) => s.saveWithVersion);
  const versions = useDcf((s) => s.versions);
  // 탭 ↔ URL hash 동기화 — 바닐라와 동일하게 #탭ID 로 공유/새로고침 보존
  const initialTab = (): TabId => {
    const h = (location.hash || "").replace(/^#/, "") as TabId;
    return TABS.some((x) => x.id === h) ? h : "summary";
  };
  const [tab, setTabState] = useState<TabId>(initialTab);
  const setTab = (id: TabId) => {
    setTabState(id);
    try { history.replaceState(null, "", location.pathname + location.search + "#" + id); } catch { /* noop */ }
  };
  const [savedFlash, setSavedFlash] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(loadChatCollapsed);
  const [chatW, setChatW] = useState(loadChatW);
  // 워크스페이스 갱신 플래시 (바닐라 flashWorkspaceRefresh — 1.8초 배지)
  const [refreshFlash, setRefreshFlash] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // 에이전트 patch 가 건드린 탭 — 탭 버튼 파란 dot (바닐라 agent-changed).
  // 바닐라는 3.6초 후 자동 소멸이지만 React 는 해당 탭을 방문할 때 해제한다.
  const patchDotTabs = useDcf((s) => s.patchDotTabs);
  const clearPatchDot = useDcf((s) => s.clearPatchDot);
  useEffect(() => {
    // 이미 보고 있는 탭이면 dot 이 의미가 없으니(셀 flash 가 이미 보임) 즉시 해제
    if (patchDotTabs.includes(tab)) clearPatchDot(tab);
  }, [patchDotTabs, tab, clearPatchDot]);
  if (!current) return null;

  const onSave = () => {
    void saveWithVersion();
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const toggleChat = (collapse: boolean) => {
    setChatCollapsed(collapse);
    try {
      localStorage.setItem(COLLAPSE_KEY, collapse ? "1" : "0");
    } catch {
      /* noop */
    }
  };

  const resizeChat = (px: number) => {
    const w = clampChatW(px);
    setChatW(w);
    try {
      localStorage.setItem(WIDTH_KEY, String(w));
    } catch {
      /* noop */
    }
  };

  const flashRefresh = () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setRefreshFlash(true);
    flashTimer.current = setTimeout(() => setRefreshFlash(false), 1800);
  };

  return (
    <div
      className={`workspace${chatCollapsed ? " chat-collapsed" : ""}`}
      style={{ "--chat-w": `${chatW}px` } as React.CSSProperties}
    >
      <button
        className="chat-reopen"
        type="button"
        title={t("chat.open")}
        aria-label={t("chat.open")}
        onClick={() => toggleChat(false)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M9 3v18" />
          <path d="m14 9 3 3-3 3" />
        </svg>
      </button>
      {/* key=프로젝트 id — 프로젝트 전환 시 채팅 캐시/인트로 재초기화 */}
      <ChatPanel
        key={current.project.id}
        onCollapse={() => toggleChat(true)}
        onResize={resizeChat}
        onWorkspaceRefresh={flashRefresh}
        onDownload={() => setTab("report")}
      />
      <div className="right-panel">
        <div className="ws-bar">
          <span className="ws-title">{current.project.name}</span>
          <span className="ws-meta">{current.project.date}</span>
          <div className="ws-actions">
            <Button variant="primary" onClick={() => void recalc()}>
              {t("btn.recalc")}
            </Button>
            <Button variant="default" onClick={onSave}>
              {savedFlash ? t("btn.saved") : t("btn.save")}
            </Button>
            <Button variant="default" onClick={() => setShowVersions((v) => !v)}>
              {t("btn.versions")} {versions.length ? `(${versions.length})` : ""}
            </Button>
            <Button variant="default" onClick={onBack}>
              {t("btn.to_dashboard")}
            </Button>
          </div>
        </div>
        {showVersions && (
          <div
            style={{
              background: "var(--white)",
              borderBottom: "var(--border)",
              padding: "10px 24px",
            }}
          >
            <VersionsPanel />
          </div>
        )}
        <div className="tabs-bar">
          {TABS.map((x) => (
            <button
              key={x.id}
              className={`tab-btn${tab === x.id ? " active" : ""}`}
              onClick={() => setTab(x.id)}
            >
              {t(x.labelKey)}
              {patchDotTabs.includes(x.id) && <span className="tab-dot" />}
            </button>
          ))}
        </div>
        {/* key=프로젝트 id — 프로젝트 전환 시 탭 로컬 상태(WACC 결과·초안 등) 초기화 */}
        <div className="tab-content" key={current.project.id}>
          <div className={`tab-pane${tab === "summary" ? " active" : ""}`}>
            <SummaryTab />
          </div>
          <div className={`tab-pane${tab === "assumptions" ? " active" : ""}`}>
            <AssumptionsTab onGoWacc={() => setTab("wacc")} />
          </div>
          <div className={`tab-pane${tab === "projection" ? " active" : ""}`}>
            <ProjectionTab />
          </div>
          <div className={`tab-pane${tab === "wacc" ? " active" : ""}`}>
            <WaccTab onApplied={() => setTab("summary")} />
          </div>
          <div className={`tab-pane${tab === "dcf" ? " active" : ""}`}>
            <ResultTab />
          </div>
          <div className={`tab-pane${tab === "sensitivity" ? " active" : ""}`}>
            <SensitivityTab />
          </div>
          <div className={`tab-pane${tab === "report" ? " active" : ""}`}>
            <ReportTab />
          </div>
        </div>
      </div>
      {/* 에이전트 patch/재계산 완료 배지 (바닐라 flashWorkspaceRefresh) */}
      <div id="ws-refresh-badge" className={refreshFlash ? "show" : ""}>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
        <span>{t("ws.refreshed")}</span>
      </div>
    </div>
  );
}
